/**
 * Notifications page showing pending team invites
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  useMediaQuery,
  useTheme,
  Snackbar,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Group as GroupIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import { useAuth } from '@/hooks/useAuth';
import type { InfiniteData } from '@tanstack/react-query';

interface PendingInvite {
  key: string;
  type: string;
  name: 'SERVICE_MANAGER' | 'SERVICE_MEMBER';
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
  user: string;
  project: {
    key: string;
    type: string;
    name: string;
  };
  team_name: string;
  created_at?: string;
}

interface PendingInvitesResponse {
  output: {
    items: PendingInvite[];
    next_page_token?: string;
    previous_page_token?: string;
    total_results?: number;
  };
}

export const Notifications: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch pending invites with infinite scroll
  // Include user key in query key to ensure proper data refresh on impersonation
  const {
    data: invitesPages,
    isLoading: isLoadingInvites,
    error: invitesError,
    fetchNextPage: fetchNextInvitesPage,
    hasNextPage: hasNextInvitesPage,
    isFetchingNextPage: isFetchingNextInvitesPage,
  } = useInfiniteQuery<
    PendingInvitesResponse,
    Error,
    InfiniteData<PendingInvitesResponse, string | undefined>,
    [string, string | undefined],
    string | undefined
  >({
    queryKey: ['pending-invites', currentUser?.key],
    queryFn: ({ pageParam }) =>
      teamApi.getPendingInvites({
        page_token: pageParam,
        max_results: 10,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined,
  });

  // Flatten invites from all pages
  const invites: PendingInvite[] = invitesPages?.pages.flatMap((page: PendingInvitesResponse) => page.output.items) ?? [];

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextInvitesPage && !isFetchingNextInvitesPage) {
          fetchNextInvitesPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextInvitesPage, isFetchingNextInvitesPage, fetchNextInvitesPage]);

  // Accept invite mutation
  const acceptMutation = useMutation({
    mutationFn: (roleUuid: string) => teamApi.acceptInvite(roleUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites', currentUser?.key] });
      queryClient.invalidateQueries({ queryKey: ['pending-invites-count', currentUser?.key] });
      queryClient.invalidateQueries({ queryKey: ['teams', currentUser?.key] });
      queryClient.invalidateQueries({ queryKey: ['team-roles'] });
      setToast({
        open: true,
        message: 'Invite accepted successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to accept invite',
        severity: 'error',
      });
    },
  });

  // Reject invite mutation
  const rejectMutation = useMutation({
    mutationFn: (roleUuid: string) => teamApi.rejectInvite(roleUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invites', currentUser?.key] });
      queryClient.invalidateQueries({ queryKey: ['pending-invites-count', currentUser?.key] });
      setToast({
        open: true,
        message: 'Invite rejected successfully',
        severity: 'success',
      });
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to reject invite',
        severity: 'error',
      });
    },
  });

  const handleAccept = (roleUuid: string) => {
    acceptMutation.mutate(roleUuid);
  };

  const handleReject = (roleUuid: string) => {
    rejectMutation.mutate(roleUuid);
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'SERVICE_MANAGER':
        return 'Service Manager';
      case 'SERVICE_MEMBER':
        return 'Service Member';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
    switch (role) {
      case 'SERVICE_MANAGER':
        return 'primary';
      case 'SERVICE_MEMBER':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Notifications
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and respond to your pending team invites.
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {isLoadingInvites ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : invitesError ? (
          <Alert severity="error">
            Failed to load notifications. Please try again.
          </Alert>
        ) : invites.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No pending invites
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You don't have any pending team invites at the moment.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {invites.map((invite) => (
              <Card key={invite.key} sx={{ boxShadow: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                      <GroupIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Team Invitation
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        You've been invited to join <strong>{invite.team_name}</strong> as a{' '}
                        <strong>{getRoleLabel(invite.name)}</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip
                          label={getRoleLabel(invite.name)}
                          color={getRoleColor(invite.name)}
                          size="small"
                        />
                        <Chip
                          label="Pending"
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                        {invite.created_at && (
                          <Typography variant="caption" color="text.secondary">
                            Invited {new Date(invite.created_at).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleAccept(invite.key)}
                    disabled={acceptMutation.isPending || rejectMutation.isPending}
                    size="small"
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => handleReject(invite.key)}
                    disabled={acceptMutation.isPending || rejectMutation.isPending}
                    size="small"
                  >
                    Reject
                  </Button>
                </CardActions>
              </Card>
            ))}

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextInvitesPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Load more trigger */}
            <div ref={loadMoreRef} style={{ height: 20 }} />
          </Box>
        )}
      </Box>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

